import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'warning' | 'error';

export interface Toast {
  id:      number;
  kind:    ToastKind;
  title:   string;
  message?: string;
  /** durée en ms avant auto-dismiss ; 0 = persistant (l'user doit fermer) */
  durationMs: number;
}

interface ToastState {
  toasts: Toast[];
  push:   (t: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
  clear:  () => void;
}

let counter = 0;

/**
 * Mini système de toasts (ticket SIMGROUP-2026-002 M-01).
 *   - Push depuis n'importe où : `useToastStore.getState().push({...})`
 *   - Affiché par `<ToastHost />` monté dans AppLayout
 *   - Auto-dismiss configurable (défaut : info=4s / success=5s / warning=8s /
 *     error=persistant jusqu'à clic)
 *
 * Utilisé par l'intercepteur axios pour rendre visibles les erreurs API
 * (avant : refus silencieux, l'utilisateur cliquait dans le vide).
 */
export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (t) => {
    const id = ++counter;
    const durationMs = t.durationMs ?? (
      t.kind === 'error'   ? 0    :
      t.kind === 'warning' ? 8000 :
      t.kind === 'success' ? 5000 :
                             4000
    );
    set((s) => ({ toasts: [...s.toasts, { ...t, id, durationMs }] }));
    if (durationMs > 0) {
      setTimeout(() => get().dismiss(id), durationMs);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear:   () => set({ toasts: [] }),
}));

/** Helpers concis pour appel depuis anywhere */
export const toast = {
  info:    (title: string, message?: string) => useToastStore.getState().push({ kind: 'info',    title, message, durationMs: 4000 }),
  success: (title: string, message?: string) => useToastStore.getState().push({ kind: 'success', title, message, durationMs: 5000 }),
  warning: (title: string, message?: string) => useToastStore.getState().push({ kind: 'warning', title, message, durationMs: 8000 }),
  error:   (title: string, message?: string) => useToastStore.getState().push({ kind: 'error',   title, message, durationMs: 0    }),
};
